import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question:
      "¿Cuáles son los tratamientos más nuevos para la depresión resistente al tratamiento?",
    answer:
      "Los tratamientos recientes incluyen SAINT® TMS acelerada (autorizada por la FDA en 2022), SPRAVATO® como monoterapia (aprobada en 2025) y la estimulación theta burst. Estas actúan por mecanismos distintos a los antidepresivos estándar. 'Más nuevo' no significa 'mejor' para cada persona — cada una tiene indicaciones y riesgos específicos.",
  },
  {
    question: "¿Es SAINT TMS un tratamiento nuevo?",
    answer:
      "SAINT® fue autorizada por la FDA en septiembre de 2022, lo que la convierte en el protocolo de TMS autorizado más reciente. Administra 50 sesiones en cinco días con orientación por fMRI. En un ensayo controlado con simulación, el 79 % del grupo activo alcanzó la remisión. Es el protocolo de tratamiento para la depresión autorizado por la FDA más rápido.",
  },
  {
    question: "¿Es SPRAVATO un tratamiento nuevo?",
    answer:
      "SPRAVATO® se aprobó por primera vez en 2019 (con un antidepresivo oral) y como monoterapia en enero de 2025. Actúa mediante la modulación del receptor NMDA — un mecanismo distinto al de los antidepresivos estándar. Se administra como aerosol nasal en consulta bajo supervisión.",
  },
  {
    question: "¿Son mejores los tratamientos más nuevos para la depresión?",
    answer:
      "No necesariamente. Los tratamientos más nuevos ofrecen opciones adicionales pero no significan automáticamente mejores resultados. Cada uno tiene indicaciones, riesgos, límites de evidencia y consideraciones de coste específicos. El tratamiento adecuado depende de sus circunstancias individuales y debe comentarse con un psiquiatra.",
  },
  {
    question:
      "¿Cuál es el tratamiento más rápido para la depresión resistente al tratamiento?",
    answer:
      "SAINT® es el protocolo de tratamiento para la depresión autorizado por la FDA más rápido — 50 sesiones en cinco días. En el estudio abierto, el tiempo medio hasta la remisión fue de 2,6 días. Sin embargo, los resultados individuales varían, y los estudios fueron pequeños con un seguimiento corto. SAINT® puede ser apropiada para la depresión resistente al tratamiento cuando se desea un calendario más rápido.",
  },
];

export const esNewTreatmentsTrd = makeEsArticle({
  slug: "new-treatments-for-treatment-resistant-depression",
  title:
    "Tratamientos nuevos para la depresión resistente al tratamiento | Neuro Wellness",
  description:
    "Tratamientos nuevos para la depresión resistente al tratamiento: SAINT® TMS acelerada, SPRAVATO® y avances recientes en estimulación cerebral para la depresión que no ha respondido a la medicación.",
  h1: "Tratamientos nuevos para la depresión resistente al tratamiento",
  breadcrumbLabel: "Tratamientos nuevos para la TRD",
  shortAnswer:
    "Los tratamientos recientes para la depresión resistente al tratamiento incluyen SAINT® TMS acelerada (autorizada por la FDA en septiembre de 2022), SPRAVATO® (esketamina, aprobada por la FDA como monoterapia en enero de 2025) y avances en la estimulación theta burst. Estos tratamientos actúan por mecanismos distintos a los antidepresivos estándar y ofrecen nuevas opciones para personas cuya depresión no ha respondido a la medicación. 'Más nuevo' no significa 'mejor' para cada persona — cada uno tiene indicaciones, riesgos y límites de evidencia específicos. Una evaluación psiquiátrica puede determinar cuál es apropiado para usted.",
  keyTakeaways: [
    "SAINT® fue autorizada por la FDA en septiembre de 2022.",
    "SPRAVATO® se aprobó como monoterapia para la TRD en enero de 2025.",
    "Estas actúan por mecanismos distintos a los antidepresivos estándar.",
    "'Más nuevo' no significa 'mejor' para cada persona.",
    "Una evaluación psiquiátrica determina cuál es apropiada.",
  ],
  sections: [
    {
      heading: "SAINT® TMS acelerada",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) fue autorizada por la FDA en septiembre de 2022 (510(k) K220177) para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos. Administra 50 sesiones en cinco días con orientación por fMRI. En un ensayo controlado con simulación, el 79 % del grupo activo alcanzó la remisión frente al 13 % del grupo de simulación. SAINT® es el protocolo de TMS autorizado por la FDA más reciente y el protocolo de tratamiento para la depresión más rápido disponible.",
      ],
    },
    {
      heading: "SPRAVATO® (esketamina)",
      paragraphs: [
        "SPRAVATO® se aprobó por primera vez en 2019 para la depresión resistente al tratamiento (con un antidepresivo oral). En enero de 2025 se aprobó como monoterapia (sin antidepresivo oral) para la depresión resistente al tratamiento. SPRAVATO® actúa mediante la modulación del receptor NMDA — un mecanismo distinto al de los antidepresivos estándar. Se administra como aerosol nasal en consulta bajo supervisión.",
      ],
    },
    {
      heading: "Estimulación theta burst",
      paragraphs: [
        "La estimulación theta burst intermitente (iTBS) fue autorizada por la FDA en 2018 como protocolo de TMS una vez al día. El ensayo THREE-D (2018) demostró que la iTBS no es inferior a la TMS estándar de 10 Hz, con sesiones mucho más cortas (3 minutos frente a 37 minutos). La iTBS es el patrón de pulsos que usa SAINT®. La estimulación theta burst representa un avance en la administración de TMS, haciendo el tratamiento más rápido y eficiente.",
      ],
    },
    {
      heading: "'Más nuevo' no significa 'mejor'",
      paragraphs: [
        "Los tratamientos más nuevos ofrecen opciones adicionales, pero no significan automáticamente mejores resultados para cada persona. Cada tratamiento tiene indicaciones, riesgos, límites de evidencia y consideraciones de coste específicos. SAINT® tiene evidencia inicial sólida pero estudios pequeños con seguimiento corto. SPRAVATO® causa disociación y requiere supervisión en consulta. El tratamiento adecuado depende de sus circunstancias individuales y debe comentarse con un psiquiatra.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. FDA. 510(k) K220177 — Magnus Neuromodulation System with SAINT Technology.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
